import { useEffect, useMemo, useRef, useState } from 'react'
import { useSession } from '@/context/AuthContext'
import { UserRound, FolderKanban, AlertTriangle, Bell } from 'lucide-react'
import { useApp } from '@/context/AppContext'
import { ProjectTasksDrawer } from '@/components/ProjectTasksDrawer'
import { RecurringTasksSection } from '@/components/RecurringTasksSection'
import { useClickOutside } from '@/lib/useClickOutside'
import { picColor, isPic } from '@/lib/utils'
import type { Project, Subtask, AssignmentNotification } from '@/lib/types'

const startOfToday = () => { const d = new Date(); d.setHours(0, 0, 0, 0); return d }
const dayDiff = (due: string) => Math.round((new Date(due.slice(0, 10) + 'T00:00:00').getTime() - startOfToday().getTime()) / 86400000)
const isOverdue = (due: string | null, done: boolean) => !!due && !done && dayDiff(due) < 0

type ColKey = 'overdue' | 'not-started' | 'in-progress' | 'done'

const COLUMNS: { key: ColKey; label: string; accent: string }[] = [
  { key: 'overdue',     label: 'Telat',       accent: '#DC2626' },
  { key: 'not-started', label: 'Not Started', accent: '#6B6860' },
  { key: 'in-progress', label: 'In Progress', accent: '#B45309' },
  { key: 'done',        label: 'Done',        accent: '#15803D' },
]

interface CardData {
  project: Project
  status: ColKey
  total: number
  done: number
  overdueCount: number
  pct: number
}

export default function MyDashboardPage() {
  const { projects, tasks, subtasks, loading, getEffectiveTaskStatus } = useApp()
  const { data: session } = useSession()
  const me = (session?.user?.username ?? '').trim()
  const meLower = me.toLowerCase()
  const firstName = (session?.user?.name ?? me).split(' ')[0]

  const [openId, setOpenId] = useState<string | null>(null)

  const board = useMemo(() => {
    if (!meLower) return null

    const mineInSub = (s: Subtask) =>
      (s.assignee ?? '').split(';').map((x) => x.trim().toLowerCase()).includes(meLower)

    // Scope "melibatkan saya": PIC project ATAU ada task/subtask di-assign ke saya
    // (konsisten dgn perhitungan project terlibat yang sudah dipakai sistem).
    const involved = new Set<string>()
    tasks.forEach((t) => { if (isPic(t.assignee, meLower)) involved.add(t.projectId) })
    subtasks.forEach((s) => {
      if (mineInSub(s)) { const t = tasks.find((x) => x.id === s.taskId); if (t) involved.add(t.projectId) }
    })
    projects.forEach((p) => { if ((p.pic ?? '').trim().toLowerCase() === meLower) involved.add(p.id) })

    const myProjects = projects.filter((p) => involved.has(p.id))

    // Status & progres project dari task NON-rutin (rutin di luar perhitungan).
    const infoOf = (pid: string): Omit<CardData, 'project'> => {
      const pts = tasks.filter((t) => t.projectId === pid && !t.recurrence)
      let done = 0, inprog = 0, overdueOpen = 0
      for (const t of pts) {
        const st = getEffectiveTaskStatus(t.id)
        if (st === 'done') done++
        else {
          if (st === 'in-progress') inprog++
          if (isOverdue(t.dueDate, false)) overdueOpen++
        }
      }
      const total = pts.length
      const pct = total > 0 ? Math.round((done / total) * 100) : 0
      let status: ColKey
      if (overdueOpen > 0) status = 'overdue'
      else if (total > 0 && done === total) status = 'done'
      else if (inprog > 0 || done > 0) status = 'in-progress'
      else status = 'not-started'
      return { status, total, done, overdueCount: overdueOpen, pct }
    }

    const cols: Record<ColKey, CardData[]> = { overdue: [], 'not-started': [], 'in-progress': [], done: [] }
    for (const p of myProjects) {
      const info = infoOf(p.id)
      cols[info.status].push({ project: p, ...info })
    }
    cols.overdue.sort((a, b) => b.overdueCount - a.overdueCount || a.project.name.localeCompare(b.project.name))
    cols['in-progress'].sort((a, b) => b.pct - a.pct || a.project.name.localeCompare(b.project.name))
    cols['not-started'].sort((a, b) => a.project.name.localeCompare(b.project.name))
    cols.done.sort((a, b) => a.project.name.localeCompare(b.project.name))

    return { cols, total: myProjects.length }
  }, [tasks, subtasks, projects, meLower, getEffectiveTaskStatus]) // eslint-disable-line react-hooks/exhaustive-deps

  // Project yang melibatkan saya — dipakai memfilter notif ("project sendiri saja").
  const myProjectIds = useMemo(() => {
    const ids = new Set<string>()
    if (!meLower) return ids
    tasks.forEach((t) => { if (isPic(t.assignee, meLower)) ids.add(t.projectId) })
    subtasks.forEach((s) => {
      if ((s.assignee ?? '').split(';').map((x) => x.trim().toLowerCase()).includes(meLower)) {
        const t = tasks.find((x) => x.id === s.taskId); if (t) ids.add(t.projectId)
      }
    })
    projects.forEach((p) => { if ((p.pic ?? '').trim().toLowerCase() === meLower) ids.add(p.id) })
    return ids
  }, [tasks, subtasks, projects, meLower])

  return (
    <div className="min-h-screen bg-[#F5F4F0]">
      {/* Header */}
      <header className="bg-white border-b border-[#E8E7E3] sticky top-0 z-30">
        <div className="px-6 flex items-center gap-3" style={{ height: '52px' }}>
          <UserRound className="w-4 h-4 text-[#6366F1]" />
          <span className="font-display text-[15px] font-bold text-[#1A1916]">My Dashboard</span>
          <div className="ml-auto flex items-center gap-2">
            {board && (
              <span className="inline-flex items-center gap-1.5 text-[11px] font-body font-semibold text-[#6B6860] bg-[#F4F3F0] border border-[#E0DFD8] px-2.5 py-0.5 rounded-full">
                <FolderKanban className="w-3 h-3" /> {board.total} project
              </span>
            )}
            {me && <NotificationsBell myProjectIds={myProjectIds} onOpenProject={setOpenId} />}
          </div>
        </div>
      </header>

      {loading ? (
        <div className="p-4 sm:p-6 animate-pulse">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 items-start">
            {[...Array(4)].map((_, c) => (
              <div key={c} className="space-y-2.5">
                <div className="h-4 w-28 bg-[#EDECE7] rounded" />
                {[...Array(2)].map((_, r) => <div key={r} className="h-20 bg-white border border-[#E8E7E3] rounded-xl" />)}
              </div>
            ))}
          </div>
        </div>
      ) : !me || !board ? (
        <div className="px-6 py-16 text-center text-[#8A8A85] font-body text-sm">
          Username belum terdaftar. Lengkapi onboarding dulu untuk melihat dashboard pribadimu.
        </div>
      ) : board.total === 0 ? (
        <div className="flex flex-col items-center justify-center px-6 py-24 text-center">
          <div className="w-12 h-12 rounded-2xl bg-white border border-[#E8E7E3] flex items-center justify-center mb-3">
            <FolderKanban className="w-5 h-5 text-[#C0BDB6]" />
          </div>
          <p className="font-display text-[15px] font-bold text-[#1A1916] mb-1">Halo, {firstName} 👋</p>
          <p className="text-[#8A8A85] font-body text-sm">Belum ada project yang melibatkanmu.</p>
        </div>
      ) : (
        <div className="p-4 sm:p-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 items-start">
            {COLUMNS.map((col) => {
              const items = board.cols[col.key]
              return (
                <div key={col.key} className="flex flex-col min-w-0">
                  <div className="flex items-center gap-2 px-1 mb-3">
                    <span className="w-2 h-2 rounded-full" style={{ backgroundColor: col.accent }} />
                    <span className="font-display text-[12px] font-bold uppercase tracking-wider" style={{ color: col.accent }}>{col.label}</span>
                    <span className="text-[11px] font-body text-[#AEADA8]">{items.length}</span>
                  </div>
                  <div className="space-y-2.5">
                    {items.length === 0 ? (
                      <p className="text-[11px] text-[#C0BDB6] font-body px-1 py-8 text-center">Tidak ada project</p>
                    ) : (
                      items.map((d) => <ProjectCard key={d.project.id} data={d} onClick={() => setOpenId(d.project.id)} />)
                    )}
                  </div>
                </div>
              )
            })}
          </div>

          <RecurringTasksSection />
        </div>
      )}

      {openId && <ProjectTasksDrawer projectId={openId} onClose={() => setOpenId(null)} />}
    </div>
  )
}

const DAY_MS = 86_400_000
const isNew = (createdAt: string) => Date.now() - new Date(createdAt).getTime() < DAY_MS

const KIND_LABEL: Record<AssignmentNotification['kind'], string> = {
  ticket_project: 'Project baru',
  ticket_done: 'Tiket selesai',
  ticket_assigned: 'Tiket untuk kamu',
  ticket_accepted: 'Tiket diterima',
  ticket_rejected: 'Tiket ditolak',
  idea_routed: 'Ide baru untuk kamu',
  project_pic: 'PIC project',
  collaborator: 'Kolaborator project',
  task: 'Task baru',
  subtask: 'Subtask baru',
  recurring: 'Task rutin',
}

// Notif: bell + popover card (klik buka detail). Cuma tampilkan notif project sendiri,
// biar gak numpuk di dashboard. Label NEW hanya < 24 jam.
function NotificationsBell({ myProjectIds, onOpenProject }: {
  myProjectIds: Set<string>
  onOpenProject: (id: string) => void
}) {
  const [items, setItems] = useState<AssignmentNotification[]>([])
  const [unread, setUnread] = useState(0)
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  useClickOutside(ref, () => setOpen(false), open)

  useEffect(() => {
    let alive = true
    fetch('/api/notifications')
      .then((r) => (r.ok ? r.json() : { items: [], unread: 0 }))
      .then((d) => { if (alive) { setItems(d.items ?? []); setUnread(Number(d.unread ?? 0)) } })
      .catch(() => {})
    return () => { alive = false }
  }, [])

  // Hanya notif yang berhubungan dgn project sendiri (atau project baru dari ticket).
  const visible = useMemo(
    () => items.filter((n) =>
      n.kind === 'ticket_done' ||
      (!!n.projectId && (myProjectIds.has(n.projectId) || n.kind === 'ticket_project')),
    ),
    [items, myProjectIds],
  )

  const toggle = () => {
    const next = !open
    setOpen(next)
    if (next && unread > 0) {
      setUnread(0)
      fetch('/api/notifications', {
        method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ all: true }),
      }).catch(() => {})
    }
  }

  return (
    <div className="relative" ref={ref}>
      <button type="button" onClick={toggle} aria-label="Notifikasi"
        className="relative w-9 h-9 flex items-center justify-center rounded-full text-[#6B6860] hover:bg-[#F4F3F0] transition-colors">
        <Bell className="w-4 h-4" />
        {unread > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-[#DC2626] text-white text-[9px] font-bold flex items-center justify-center">
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-2 w-80 max-h-96 overflow-auto bg-white border border-[#E8E7E3] rounded-xl shadow-xl z-50">
          <div className="px-3.5 py-2.5 border-b border-[#F2F1EC] flex items-center gap-2 sticky top-0 bg-white">
            <Bell className="w-3.5 h-3.5 text-[#6366F1]" />
            <span className="font-display text-[12px] font-bold uppercase tracking-wider text-[#6B6860]">Notifikasi</span>
          </div>
          {visible.length === 0 ? (
            <p className="px-3.5 py-8 text-center text-[12px] text-[#AEADA8] font-body">Belum ada notifikasi.</p>
          ) : (
            <ul className="p-1.5">
              {visible.map((n) => {
                const fresh = isNew(n.createdAt)
                return (
                  <li key={n.id}>
                    <button type="button"
                      onClick={() => { if (n.projectId) { onOpenProject(n.projectId); setOpen(false) } }}
                      className="w-full text-left flex items-start gap-2 rounded-lg px-2.5 py-2 min-h-[44px] hover:bg-[#F4F3F0] transition-colors">
                      {fresh && (
                        <span className="mt-0.5 text-[9px] font-body font-bold text-white bg-[#6366F1] px-1.5 py-0.5 rounded flex-shrink-0">NEW</span>
                      )}
                      <span className="flex-1 min-w-0 text-[12.5px] font-body text-[#1A1916]">
                        <span className="text-[#6B6860]">{KIND_LABEL[n.kind]}:</span>{' '}
                        <span className="font-semibold">{n.projectName ?? n.title ?? '—'}</span>
                        {n.assignerName && <span className="text-[#8A8A85]"> · oleh {n.assignerName}</span>}
                      </span>
                    </button>
                  </li>
                )
              })}
            </ul>
          )}
        </div>
      )}
    </div>
  )
}

function ProjectCard({ data, onClick }: { data: CardData; onClick: () => void }) {
  const p = data.project
  return (
    <button
      onClick={onClick}
      className="w-full text-left bg-white border border-[#E8E7E3] rounded-xl p-3.5 hover:border-[#C8C7BF] hover:shadow-sm transition-all"
    >
      <div className="flex items-center gap-2 mb-2.5">
        <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: p.color }} />
        <span className="flex-1 min-w-0 text-[13.5px] font-body font-semibold text-[#1A1916] truncate">{p.name}</span>
        {p.pic && (
          <span
            className="w-5 h-5 rounded-full flex items-center justify-center text-[9px] font-bold text-white flex-shrink-0"
            style={{ backgroundColor: picColor(p.pic) }}
            title={`PIC: ${p.pic}`}
          >
            {p.pic.charAt(0).toUpperCase()}
          </span>
        )}
      </div>

      {data.overdueCount > 0 && (
        <span className="inline-flex items-center gap-1 text-[10px] font-body font-bold text-[#DC2626] bg-[#FEF2F2] border border-[#FECACA] px-1.5 py-0.5 rounded mb-2.5">
          <AlertTriangle className="w-3 h-3" /> {data.overdueCount} telat
        </span>
      )}

      <div className="flex items-center gap-2">
        <span className="flex-1 h-1.5 rounded-full bg-[#F0EFE9] overflow-hidden">
          <span className="block h-full rounded-full" style={{ width: `${data.pct}%`, backgroundColor: p.color }} />
        </span>
        <span className="text-[11px] font-body text-[#8A8A85] flex-shrink-0">{data.done}/{data.total}</span>
        <span className="text-[11px] font-body font-bold text-[#1A1916] flex-shrink-0 w-9 text-right">{data.pct}%</span>
      </div>
    </button>
  )
}
